import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";

export function areAllPasswordRequirementsMet(password: string): boolean {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password)
  );
}

interface PasswordRequirementsProps {
  password: string;
}

export function PasswordRequirements({ password }: PasswordRequirementsProps) {
  const { t } = useTranslation();

  const requirements = [
    {
      key: "length",
      label: t("auth.passwordRequirements.length"),
      met: password.length >= 8,
    },
    {
      key: "uppercase",
      label: t("auth.passwordRequirements.uppercase"),
      met: /[A-Z]/.test(password),
    },
    {
      key: "lowercase",
      label: t("auth.passwordRequirements.lowercase"),
      met: /[a-z]/.test(password),
    },
    {
      key: "number",
      label: t("auth.passwordRequirements.number"),
      met: /[0-9]/.test(password),
    },
  ];

  const metCount = requirements.filter((requirement) => requirement.met).length;
  const progress = (metCount / requirements.length) * 100;

  return (
    <div className="mt-3 space-y-2.5" aria-live="polite">
      <div className="h-1 w-full overflow-hidden rounded-full bg-bg-quaternary dark:bg-bg-quaternary">
        <div
          className="h-full rounded-full bg-[var(--colors-background-bg-success-600)] transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>
      <ul className="grid grid-cols-2 gap-2">
        {requirements.map((requirement) => (
          <li
            key={requirement.key}
            className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-xs transition-colors ${
              requirement.met
                ? "border-[var(--colors-border-border-success)] bg-[var(--colors-background-bg-success-50)] text-text-success dark:text-text-success"
                : "border-border-secondary dark:border-border-secondary bg-bg-secondary dark:bg-bg-secondary text-text-tertiary dark:text-text-tertiary"
            }`}
          >
            {requirement.met ? (
              <Check className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                <span className="h-1.5 w-1.5 rounded-full bg-text-quaternary dark:bg-text-quaternary" />
              </span>
            )}
            <span className="leading-tight">{requirement.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
